<template>
  <div id="ngb-member-list" class="ngb-list-wrap">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
      :LoadingText="OverlayLoadingText"
    ></overlay-loading-progress>

    <v-card class="elevation-6 rounded-lg">
      <v-sheet class="list-header" tile>
        <div class="header-top">
          <!-- Top Left: Title with Icon -->
          <div class="header-title-section">
            <div class="title-icon-wrapper">
              <v-icon large color="white" class="title-icon"
                >mdi-account-tie</v-icon
              >
            </div>
            <div class="title-content">
              <h2 class="page-title">{{ PageTitle }}</h2>
              <p class="page-desc">{{ PageDescription }}</p>
            </div>
          </div>

          <!-- Top Right: Actions -->
          <div class="header-actions-top">
            <v-chip
              small
              color="rgba(255, 255, 255, 0.25)"
              text-color="white"
              class="year-chip-top"
            >
              <v-icon left x-small>mdi-calendar-check</v-icon>
              {{ YearName }}
            </v-chip>

            <v-btn
              icon
              small
              color="white"
              class="action-btn"
              @click.prevent="refreshPageData"
              v-tooltip="'Refresh List'"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>

            <v-btn
              color="white"
              small
              class="action-btn-primary"
              @click.prevent="addRecordPrompt = true"
            >
              <v-icon left small>mdi-plus-circle</v-icon> Assign Member
            </v-btn>
          </div>
        </div>

        <!-- Bottom: Search Bar with Flying Icons -->
        <div class="header-search">
          <div class="flying-icons-container">
            <div class="flying-icon" style="--delay: 0s; --duration: 4s">
              <v-icon x-big color="white" style="opacity: 0.3"
                >mdi-account</v-icon
              >
            </div>
            <div class="flying-icon" style="--delay: 1s; --duration: 5s">
              <v-icon x-big color="white" style="opacity: 0.25"
                >mdi-briefcase</v-icon
              >
            </div>
            <div class="flying-icon" style="--delay: 2s; --duration: 4.5s">
              <v-icon x-big color="white" style="opacity: 0.2">mdi-star</v-icon>
            </div>
            <div class="flying-icon" style="--delay: 0.5s; --duration: 5.5s">
              <v-icon x-big color="white" style="opacity: 0.28"
                >mdi-target</v-icon
              >
            </div>
          </div>
          <v-text-field
            full-width
            v-model="search"
            prepend-icon="mdi-magnify"
            dense
            hide-details
            rounded
            clearable
            placeholder="Search by member name, designation, or LOM..."
            class="search-input-modern"
          ></v-text-field>
        </div>
      </v-sheet>

      <v-card-text>
        <div class="loading-wrap" v-if="LoadingFlag">
          <v-progress-circular
            indeterminate
            size="44"
            color="primary"
          ></v-progress-circular>
        </div>

        <v-container fluid>
          <v-row class="summary-row" align="center" justify="space-between">
            <v-col cols="12" md="6">
              <div class="found-text" v-if="rows && rows.length > 0">
                <v-icon small color="success" class="mr-2"
                  >mdi-check-circle</v-icon
                >
                <strong class="text-primary">{{ rows.length }}</strong>
                member(s) assigned.
              </div>
              <div v-else-if="ResultFlag && rows.length == 0" class="muted">
                <v-icon small color="warning" class="mr-2"
                  >mdi-alert-circle</v-icon
                >
                No members assigned yet.
              </div>
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="text-right d-none d-md-flex justify-end"
            >
              <v-btn
                v-if="ExcelDownloadFlag"
                color="success"
                small
                class="mr-2"
                @click.prevent="
                  generateExcel(
                    tableData1,
                    tableOptions1.ExcelFields,
                    tableOptions1.ExcelFileName,
                    'My Worksheet'
                  )
                "
              >
                <v-icon left x-small>mdi-file-excel</v-icon> Download
              </v-btn>
              <v-chip small color="indigo lighten-5" text-color="indigo">
                <v-icon left x-small color="indigo"
                  >mdi-account-multiple-check</v-icon
                >
                {{ rows.length || 0 }} Total
              </v-chip>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-data-iterator
                :items="filteredRows"
                :items-per-page="-1"
                hide-default-footer
              >
                <template v-slot:default="props">
                  <v-row dense>
                    <v-col
                      v-for="item in props.items"
                      :key="item.MemberId"
                      cols="12"
                      sm="6"
                      md="4"
                      lg="3"
                    >
                      <v-card class="member-card" hover elevation="4">
                         <!-- Active Status Chip -->
                          <div class="info-row mt-3 align-left-end">
                            <v-chip
                              x-small
                              :color="
                                item.ActiveStatus === 1
                                  ? 'success'
                                  : item.ActiveStatus === 2
                                  ? 'warning'
                                  : 'error'
                              "
                              text-color="white"
                              class="status-chip"
                            >
                              <v-icon left x-small>
                                {{
                                  item.ActiveStatus === 1
                                    ? "mdi-check-circle"
                                    : item.ActiveStatus === 2
                                    ? "mdi-clock-outline"
                                    : "mdi-alert-circle"
                                }}
                              </v-icon>
                              {{ item.ActiveStatusTxt }}
                            </v-chip>
                          </div>

                        <div class="card-top d-flex">
                          <v-avatar size="88" class="avatar-elev">
                            <v-img
                              :src="item.ProfilePic"
                              :alt="item.MemberFullname"
                            />
                          </v-avatar>

                          <div class="card-meta pl-4">
                            <div class="member-name">
                              {{ item.MemberFullname }}
                            </div>
                            <div class="member-designation caption">
                              <v-icon x-small class="mr-1"
                                >mdi-badge-account</v-icon
                              >
                              {{ item.DesignationName || "—" }}
                            </div>
                            <div class="member-lom caption">
                              <v-icon x-small class="mr-1"
                                >mdi-map-marker</v-icon
                              >
                              {{ item.LomName || "—" }}
                            </div>
                          </div>
                        </div>

                        <v-divider class="my-3"></v-divider>

                        <div class="card-body">
                          <div class="info-row" v-if="RoleId == 2">
                            <v-icon x-small color="primary" class="mr-1"
                              >mdi-identifier</v-icon
                            >
                            <span class="info-label">Membership:</span>
                            <span class="info-value">{{
                              item.MembershipId
                            }}</span>
                          </div>
                          <div class="info-row">
                            <v-icon x-small color="warning" class="mr-1"
                              >mdi-office-building</v-icon
                            >
                            <span class="info-label">Zone:</span>
                            <span class="info-value">{{ item.ZoneName }}</span>
                          </div>
                          <div class="info-row" v-if="RoleId == 2">
                            <v-icon x-small color="orange" class="mr-1"
                              >mdi-phone</v-icon
                            >
                            <span class="info-label">Mobile:</span>
                            <span class="info-value">{{ item.MobileNo }}</span>
                          </div>
                          <div class="info-row" v-if="RoleId == 2">
                            <v-icon x-small color="success" class="mr-1"
                              >mdi-email</v-icon
                            >
                            <span class="info-label">Email:</span>
                            <span
                              class="info-value"
                              style="word-break: break-all"
                              >{{ item.EmailId }}</span
                            >
                          </div>
                        </div>

                        <v-card-actions class="card-actions">
                          <v-tooltip
                            bottom
                            v-if="item.DesignationId != 4 && editFlag"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="primary"
                                v-bind="attrs"
                                v-on="on"
                                @click="editData(item)"
                                class="edit-btn"
                              >
                                <v-icon>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit Member</span>
                          </v-tooltip>

                          <v-tooltip bottom v-if="DeleteFlag">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                small
                                color="red"
                                v-bind="attrs"
                                v-on="on"
                                @click="deleteData(item)"
                                class="delete-btn"
                              >
                                <v-icon>mdi-delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete Member</span>
                          </v-tooltip>

                          <v-spacer></v-spacer>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                </template>
              </v-data-iterator>
            </v-col>
          </v-row>

          <v-row class="notes-row mt-6">
            <v-col cols="12" md="8">
              <h4 class="notes-title">Important</h4>
              <ol class="notes-content">
                <li>All elective post members should be active members.</li>
                <li>
                  Elective officers must upload age proof under their profile
                  update page.
                </li>
                <li>Use "Assign Member" to add or update Year assignments.</li>
              </ol>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-card>

    <!-- Add Dialog -->
    <v-dialog v-model="addRecordPrompt" persistent scrollable max-width="75%">
      <div style="max-height: 80vh; overflow-y: auto">
        <new-ngb-team-members-modify
          pageDescription="Assign NGB members"
          :addRecordPrompt="addRecordPrompt"
          @hideAddRecordPrompt="hideAddRecordPrompt"
          v-if="addRecordPrompt"
          boardType="1"
        ></new-ngb-team-members-modify>
      </div>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editRecordPrompt" persistent scrollable max-width="75%">
      <div style="max-height: 80vh; overflow-y: auto">
        <ngb-team-members-edit
          pageDescription="Edit NGB members"
          :editRecordPrompt="editRecordPrompt"
          :recordData="selectedData"
          @hideEditRecordPrompt="hideEditRecordPrompt"
          v-if="editRecordPrompt"
        ></ngb-team-members-edit>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import NewNgbTeamMembersModify from "@/view/pages/erp/team-members/new-ngb/NewNgbTeamMembersModify.vue";
import NgbTeamMembersEdit from "@/view/pages/erp/team-members/ngb/NgbTeamMembersEdit.vue";

export default {
  mixins: [common],
  components: {
    NewNgbTeamMembersModify,
    NgbTeamMembersEdit,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      ResultFlag: false,
      DeleteFlag: true,
      ExcelDownloadFlag: false,
      ExcelFileName: "",
      tableOptions1: [],
      tableData1: [],

      OverlayLoadingFlag: false,
      OverlayLoadingText: "",

      refreshButtonFlag: false,
      floatButtonFlag: false,

      rows: [],
      tableSelectFlag1: false,
      tableColumns1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      editFlag: true,
      selectedData: {},

      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,

      zone: {},
      lom: {},

      name: "",
      nameRules: [
        (v) => !!v || "Name is required",
        (v) => (v && v.length <= 10) || "Name must be less than 10 characters",
      ],
      email: "",
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      select: "Item 1",
      items: ["Item 1", "Item 2", "Item 3", "Item 4"],
      checkbox: false,

      FromZoneCodeRules: [(v) => !!v || "Zone is required"],
      FromZoneCode: "",
      FromZoneCodeOptions: [],
      FromZoneCodeOptionsLoading: false,

      FromLomCodeRules: [(v) => !!v || "LOM is required"],
      FromLomCode: "",
      FromLomCodeOptions: [],
      FromLomCodeOptionsLoading: false,

      ToZoneCodeRules: [(v) => !!v || "Zone is required"],
      ToZoneCode: "",
      ToZoneCodeOptions: [],
      ToZoneCodeOptionsLoading: false,

      ToLomCodeRules: [(v) => !!v || "LOM is required"],
      ToLomCode: "",
      ToLomCodeOptions: [],
      ToLomCodeOptionsLoading: false,

      SearchId: "",
      SearchIdRules: [(v) => !!v || "Search Type is required"],
      SearchIdOptions: [
        { value: "", text: "--Select Type--" },
        { value: 1, text: "By LOM" },
        { value: 2, text: "By Membership Id" },
      ],

      MembershipId: "",
      MembershipIdRules: [(v) => !!v || "Membership Id is required"],

      YearHalfId: "",
      YearHalfIdRules: [(v) => !!v || "Year Half is required"],
      YearHalfIdOptions: [
        { value: "", text: "--Select Half--" },
        { value: "First", text: "First Half" },
        { value: "Second", text: "Second Half" },
      ],

      YearIdRules: [(v) => !!v || "Year is required"],
      YearId: "",
      YearIdOptions: [],
      YearIdOptionsLoading: false,

      ApprovalDescription: "",
      ApprovalDescriptionRules: [],

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      RoleId: "",
    };
  },
  computed: {
    filteredRows() {
      if (!this.search) {
        return this.rows;
      }
      const query = this.search.toLowerCase().trim();
      return this.rows.filter((item) => {
        return (
          (item.MemberFullname &&
            item.MemberFullname.toLowerCase().includes(query)) ||
          (item.DesignationName &&
            item.DesignationName.toLowerCase().includes(query)) ||
          (item.LomName && item.LomName.toLowerCase().includes(query)) ||
          (item.MembershipId && item.MembershipId.toLowerCase().includes(query))
        );
      });
    },
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        this.RoleId = RoleId;

        this.pageData();
      }
    },
    SearchId: function () {
      console.log("watch SearchId");
      this.rows = [];
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "ngb",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getTableRecords();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    hideAddRecordPrompt(flag) {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      console.log(flag);
      if (flag) {
        this.getTableRecords();
      }
    },
    hideEditRecordPrompt(flag) {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      if (flag) {
        this.getTableRecords();
      }
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedData = tr;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one member to edit";
        this.toast("error", message);
      }
    },
    resetForm() {
      this.search = "";
      this.rows = [];
      this.selected = [];
      this.$refs.form3.reset();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    getTableRecords() {
      console.log("getTableRecords called");

      var LomCode = this.$session.get("LomId");
      LomCode = LomCode == (null || undefined) ? "" : LomCode;
      console.log("LomCode=" + LomCode);

      if (LomCode != null) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-designation/lists";
        var upload = {
          UserInterface: 3,
          OrganizationTypeId: 1,
          YearCode: this.JciYearCode,
          ExceptDesignationId: [1, 5, 7],
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.rows = [];
        this.ResultFlag = false;
        this.LoadingFlag = true;
        this.ExcelDownloadFlag = false;
        this.ExcelFileName = "";
        this.tableData1 = [];

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.ResultFlag = true;
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            var options = records.TableOptions;
            thisIns.tableOptions1 = options;
            if (flag == 1) {
              thisIns.rows = records.TableData;
              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableSelectFlag1 = records.TableSelectFlag;
              thisIns.DeleteFlag = records.TableDeleteFlag;
              thisIns.tableData1 = records.TableData;
              thisIns.ExcelDownloadFlag = options.DownloadFlag;
              thisIns.ExcelFileName = options.ExcelFileName;
              thisIns.ExcelFields = options.ExcelFields;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.toast("error", error);
            thisIns.ResultFlag = true;
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Your session is expired. Kindly login again. ";
        console.log("error=" + message);
      }
    },
    getZoneCodeOptions(source, destination) {
      console.log("getZoneCodeOptions called");
      // this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
      var CurrentYearId = this.CurrentYearId;
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", CurrentYearId=" +
          CurrentYearId
      );
      if (source != "" && destination != "") {
        this.LoadingFlag = true;
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/year-wise-lom/zone-options";
        var selectbox1_conditions_array = {
          UserInterface: 2,
          YearCode: CurrentYearId,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      }
    },
    getLomCodeOptions(source, destination, ZoneCode) {
      console.log("getLomCodeOptions called");
      console.log(
        "source=" +
          source +
          ", destination=" +
          destination +
          ", ZoneCode=" +
          ZoneCode
      );
      var CurrentYearId = this.CurrentYearId;
      if (ZoneCode != "" && CurrentYearId != "") {
        var selectbox1_source = source;
        var selectbox1_destination = destination;
        var selectbox1_url = "api/year-wise-lom/lom-options";
        var selectbox1_conditions_array = {
          UserInterface: 1,
          YearCode: CurrentYearId,
          ZoneCode: ZoneCode,
        };
        this.getSelectBoxOptions(
          selectbox1_source,
          selectbox1_destination,
          selectbox1_conditions_array,
          selectbox1_url
        );
      } else {
        var message = "Zone should not be empty";
        this.toast("error", message);
      }
    },
    deleteData(tr) {
      console.log("deleteData called");
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });
      if (n1 > 0) {
        this.deleteRecord(tr);
      } else {
        var message = "Kindly select one member to delete";
        this.toast("error", message);
      }
    },
    deleteRecord(tr) {
      // add todo in store and clear all fields in dialog
      console.log("deleteRecord called");

      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });

      if (n1 > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/year-wise-designation/member-delete";
        var upload = {
          UserInterface: 1,
          OrganizationType: 1,
          Year: this.JciYearCode,
          Zone: tr.ZoneId,
          Lom: tr.LomId,
          Member: tr.MemberId,
          Designation: tr.DesignationId,
          YearwiseOfficesHeld: tr.YearwiseOfficesHeldId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        // thisIns.pageLoadingOn()

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, false);
              thisIns.getTableRecords();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        console.log("some errors occured");
        var message = "Kindly select one member to delete";
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss">
/* filepath: /home/hplap/Documents/web_app/jci_india_erp_app_new/src/view/pages/erp/team-members/ngb/NgbTeamMembersList.vue */
/* Professional & Beautiful UI: Modern, clean, responsive with animations */

/* Keyframe Animations */
@keyframes flyingIcon {
  0%,
  100% {
    transform: translateY(0px) scale(1);
    opacity: 1;
  }
  25% {
    transform: translateY(-12px) scale(1.05);
  }
  50% {
    transform: translateY(-20px) scale(1.1);
  }
  75% {
    transform: translateY(-12px) scale(1.05);
  }
}

@keyframes floatingIcons {
  0% {
    transform: translateY(100px) translateX(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) translateX(30px) rotate(360deg);
    opacity: 0;
  }
}

@keyframes floatingIconsAlt {
  0% {
    transform: translateY(100px) translateX(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(-120px) translateX(-40px) rotate(-360deg);
    opacity: 0;
  }
}

@keyframes glow {
  0%,
  100% {
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.3),
      0 0 20px rgba(25, 118, 210, 0.3);
  }
  50% {
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.5),
      0 0 30px rgba(25, 118, 210, 0.5);
  }
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cardGlide {
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes buttonPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}

#ngb-member-list {
  font-family: "Inter", "Roboto", sans-serif;
  animation: slideInUp 0.6s ease-out;

  .list-header {
    background: linear-gradient(135deg, #05213d 0%, #1663a3 50%, #122442 100%);
    padding: 24px 28px;
    border-radius: 16px;
    margin-bottom: 24px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 12px 35px rgba(25, 118, 210, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.15);

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 500px;
      height: 500px;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: spin 15s linear infinite;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.05) 50%,
        transparent 100%
      );
      animation: shimmer 3s infinite;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    margin-bottom: 18px;
    position: relative;
    z-index: 2;
  }

  .header-title-section {
    display: flex;
    align-items: center;
    gap: 18px;
    flex: 1;
    min-width: 0;
  }

  .title-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 14px;
    padding: 12px;
    flex-shrink: 0;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
      animation: glow 2s ease-in-out infinite;
    }

    .title-icon {
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
    }
  }

  .title-content {
    min-width: 0;
  }

  .page-title {
    margin: 0;
    font-size: 24px;
    font-weight: 800;
    color: #ffffff;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    letter-spacing: -0.5px;
  }

  .page-desc {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    font-weight: 500;
    margin-top: 4px;
    letter-spacing: 0.3px;
  }

  .header-actions-top {
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
    flex-wrap: wrap;
    justify-content: flex-end;
    position: relative;
    z-index: 2;
  }

  .year-chip-top {
    background: rgba(255, 255, 255, 0.2) !important;
    color: #ffffff !important;
    border: 1px solid rgba(255, 255, 255, 0.3);
    backdrop-filter: blur(8px);
    transition: all 0.3s ease;
    font-weight: 600;

    &:hover {
      background: rgba(255, 255, 255, 0.3) !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }

  .action-btn {
    color: white !important;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      transform: translate(-50%, -50%) scale(0);
      transition: transform 0.6s ease;
    }

    &:hover {
      transform: scale(1.15) rotate(5deg);
      box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);

      &::before {
        transform: translate(-50%, -50%) scale(1);
      }
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .action-btn-primary {
    background: rgba(255, 255, 255, 0.18) !important;
    color: #ffffff !important;
    border-radius: 8px;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 1px solid rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(8px);
    font-weight: 600;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(255, 255, 255, 0.2) 50%,
        transparent 100%
      );
      transition: left 0.5s ease;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.28) !important;
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
      border-color: rgba(255, 255, 255, 0.35);

      &::before {
        left: 100%;
      }
    }

    &:active {
      transform: translateY(-1px);
    }
  }

  .header-search {
    padding-top: 8px;
    position: relative;
    z-index: 2;
  }

  .flying-icons-container {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 120px;
    pointer-events: none;
    overflow: hidden;
    z-index: 1;
  }

  .flying-icon {
    position: absolute;
    bottom: 0;
    left: 10%;
    animation: floatingIcons var(--duration, 4s) ease-in var(--delay, 0s)
      infinite;

    &:nth-child(2) {
      left: 25%;
      animation: floatingIconsAlt var(--duration, 5s) ease-in var(--delay, 1s)
        infinite;
    }

    &:nth-child(3) {
      left: 40%;
      animation: floatingIcons var(--duration, 4.5s) ease-in var(--delay, 2s)
        infinite;
    }

    &:nth-child(4) {
      left: 55%;
      animation: floatingIconsAlt var(--duration, 5.5s) ease-in
        var(--delay, 0.5s) infinite;
    }
  }

  .search-input-modern {
    background: rgba(255, 255, 255, 0.12) !important;
    border-radius: 10px !important;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    position: relative;
    z-index: 3;
    max-width: 450px; /* adjust: 250px / 300px / 400px */
    &:focus-within {
      background: rgba(255, 255, 255, 0.18) !important;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
      transform: translateY(-2px);
      border-color: rgba(255, 255, 255, 0.3) !important;
    }
  }

  /* Direct input element targeting */
  .search-input-modern input {
    color: #ffffff !important;
    caret-color: #ffffff !important;
    -webkit-text-fill-color: #ffffff !important;
  }

  .search-input-modern input::placeholder {
    color: rgba(255, 255, 255, 0.7) !important;
    -webkit-text-fill-color: rgba(255, 255, 255, 0.7) !important;
    opacity: 1 !important;
  }

  .search-input-modern ::-webkit-input-placeholder {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  .search-input-modern ::-moz-placeholder {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  .search-input-modern :-ms-input-placeholder {
    color: rgba(255, 255, 255, 0.7) !important;
  }

  /* Vuetify v-input penetration */
  .search-input-modern ::v-deep .v-input__slot {
    color: #ffffff !important;
  }

  .search-input-modern ::v-deep input {
    color: #ffffff !important;
    caret-color: #ffffff !important;
    -webkit-text-fill-color: #ffffff !important;
  }

  .search-input-modern ::v-deep .v-text-field__input {
    color: #ffffff !important;
    caret-color: #ffffff !important;
  }

  .search-input-modern ::v-deep .v-input__prepend-inner {
    color: #ffffff !important;
  }

  .search-input-modern ::v-deep .v-input__append-inner {
    color: #ffffff !important;
  }

  .search-input-modern ::v-deep .v-icon {
    color: #ffffff !important;
  }

  .search-input-modern ::v-deep .v-input__append-inner .v-icon {
    color: #ffffff !important;
  }

  .search-input-modern ::v-deep .mdi::before {
    color: #ffffff !important;
  }

  .loading-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 60px 20px;
  }

  .summary-row {
    margin-bottom: 24px;
    padding: 16px 0;
    border-bottom: 2px solid #f0f1f3;
    animation: slideInUp 0.6s ease-out 0.2s both;
  }

  .found-text {
    color: #1f2937;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
  }

  .muted {
    color: #6b7280;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
  }

  .member-card {
    border-radius: 16px;
    overflow: hidden;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06), 0 2px 8px rgba(0, 0, 0, 0.04);
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    border: 1px solid rgba(25, 118, 210, 0.05);
    animation: cardGlide 0.5s ease-out both;

    &:nth-child(1) {
      animation-delay: 0.05s;
    }
    &:nth-child(2) {
      animation-delay: 0.1s;
    }
    &:nth-child(3) {
      animation-delay: 0.15s;
    }
    &:nth-child(4) {
      animation-delay: 0.2s;
    }

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: linear-gradient(90deg, #092c4e, #2196f3, #4e8ef7);
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.3s ease;
      z-index: 1;
    }

    &:hover {
      transform: translateY(-12px) scale(1.02);
      box-shadow: 0 20px 40px rgba(25, 118, 210, 0.15),
        0 8px 16px rgba(0, 0, 0, 0.08);
      border-color: rgba(25, 118, 210, 0.15);

      &::before {
        transform: scaleX(1);
      }

      .position-chip {
        animation: buttonPulse 1.5s infinite;
      }
    }
  }

  .card-header-badge {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 10;
  }

  .position-chip {
    font-weight: 700;
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.8px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .card-top {
    display: flex;
    align-items: center;
    padding: 20px 16px;
    padding-top: 20px;
    background: linear-gradient(
      135deg,
      transparent 0%,
      rgba(25, 118, 210, 0.02) 100%
    );
  }

  .avatar-elev {
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
    flex-shrink: 0;
    border: 3px solid rgba(25, 118, 210, 0.1);
    transition: all 0.3s ease;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      inset: 0;
      background: radial-gradient(
        circle at 30% 30%,
        rgba(255, 255, 255, 0.3),
        transparent 50%
      );
      border-radius: 14px;
    }

    img {
      transition: transform 0.3s ease;
    }

    &:hover {
      box-shadow: 0 16px 36px rgba(25, 118, 210, 0.2);
      transform: scale(1.05);

      img {
        transform: scale(1.08);
      }
    }
  }

  .card-meta {
    flex: 1;
    min-width: 0;
  }

  .member-name {
    font-size: 16px;
    font-weight: 800;
    color: #0f172a;
    word-break: break-word;
    background: linear-gradient(135deg, #1976d2, #2196f3);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .member-designation {
    color: #1976d2;
    margin-top: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 13px;
  }

  .member-lom {
    color: #d32f2f;
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
    font-size: 13px;
  }

  .card-body {
    padding: 14px 16px;
    border-top: 1px solid rgba(25, 118, 210, 0.05);
    background: rgba(25, 118, 210, 0.01);
  }

  .info-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #4b5563;
    margin-bottom: 8px;
    font-size: 12px;
    line-height: 1.5;
    transition: all 0.2s ease;

    &:hover {
      color: #1976d2;
      padding-left: 4px;
    }
  }

  .info-label {
    font-weight: 700;
    color: #374151;
    min-width: 70px;
  }

  .info-value {
    color: #6b7280;
    flex: 1;
    font-weight: 500;
  }

  .card-actions {
    padding: 12px 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    border-top: 1px solid rgba(25, 118, 210, 0.05);
    background: rgba(25, 118, 210, 0.02);
    transition: all 0.3s ease;
  }

  .member-card:hover .card-actions {
    background: rgba(25, 118, 210, 0.06);
  }

  .edit-btn,
  .delete-btn {
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border-radius: 8px;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      height: 100%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.3),
        transparent 70%
      );
      border-radius: 8px;
      transform: translate(-50%, -50%) scale(0);
      transition: transform 0.5s ease;
      pointer-events: none;
    }

    &:hover {
      transform: scale(1.25) rotate(-5deg);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);

      &::before {
        transform: translate(-50%, -50%) scale(1);
      }
    }

    &:active {
      transform: scale(1.1);
    }
  }

  .notes-row {
    margin-top: 32px;
    animation: slideInUp 0.6s ease-out 0.4s both;
  }

  .notes-title {
    font-size: 17px;
    font-weight: 800;
    color: #111827;
    margin-bottom: 14px;
    position: relative;
    padding-left: 12px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 20px;
      background: linear-gradient(180deg, #1976d2, #2196f3);
      border-radius: 2px;
    }
  }

  .notes-content {
    padding-left: 24px;
    color: #4b5563;
    line-height: 1.9;
    font-weight: 500;

    li {
      margin-bottom: 10px;
      transition: all 0.2s ease;

      &:hover {
        color: #1976d2;
        padding-left: 8px;
      }
    }
  }

  /* Responsive */
  @media (max-width: 960px) {
    .list-header {
      padding: 18px 20px;
      border-radius: 12px;
    }

    .header-top {
      flex-direction: column;
      align-items: flex-start;
      gap: 14px;
    }

    .header-title-section {
      width: 100%;
    }

    .page-title {
      font-size: 20px;
    }

    .header-actions-top {
      width: 100%;
      justify-content: flex-start;
    }

    .action-btn-primary {
      flex: 1;
    }
  }

  @media (max-width: 600px) {
    .page-title {
      font-size: 18px;
    }

    .page-desc {
      font-size: 12px;
    }

    .header-actions-top {
      gap: 8px;
      width: 100%;
    }

    .title-icon-wrapper {
      display: flex;
      padding: 10px;
    }

    .member-card {
      margin-bottom: 12px;
      border-radius: 12px;
    }

    .member-card:hover {
      transform: translateY(-8px) scale(1.01);
    }
  }
}
</style>